import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	TextField,
	InputAdornment,
	IconButton,
	FormControlLabel,
	Switch,
	Tabs,
	Tab,
	Typography
  } from '@material-ui/core';
  
import { Visibility, VisibilityOff } from '@material-ui/icons';

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";
import UserTeamsField from "./UserTeamsField";
import { getAccessSnapshot } from "../../context/AccessControl/accessSnapshot";
import {
  listUserGroups,
  listRoles,
  getUserTeams,
  setUserTeams
} from "../../services/access.api";

import { getUser, createUser, updateUser } from "../../services/users.api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(2),
		"& > *": {
			flex: 1,
		},
	},
	singleFieldLine: {
		marginBottom: theme.spacing(2),
	},
	// ✅ CORREGIDO: FormSection dinámico según tema
	formSection: {
		marginBottom: theme.spacing(3),
		padding: theme.spacing(2),
		borderRadius: '8px',
		backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
		border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
	},
	// ✅ CORREGIDO: SectionTitle dinámico según tema
	sectionTitle: {
		fontSize: '16px',
		fontWeight: 'bold',
		color: theme.palette.text.primary,
		marginBottom: theme.spacing(2),
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
	},
	btnWrapper: {
		position: "relative",
		display: 'flex',
		gap: theme.spacing(2),
		justifyContent: 'flex-end',
		marginTop: theme.spacing(3),
	},
	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	maxWidth: {
		width: '100%',
	},
	textField: {
		marginBottom: theme.spacing(2),
	},
	saveButton: {
		backgroundColor: '#1976d2',
		color: 'white',
		borderRadius: '8px',
		padding: '8px 24px',
		fontWeight: 'bold',
		textTransform: 'none',
		boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
		'&:hover': {
			backgroundColor: '#1565c0',
			transform: 'translateY(-1px)',
			boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
		},
	},
	cancelButton: {
		backgroundColor: '#f44336',
		color: 'white',
		borderRadius: '8px',
		padding: '8px 24px',
		fontWeight: 'bold',
		textTransform: 'none',
		boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
		'&:hover': {
			backgroundColor: '#d32f2f',
			transform: 'translateY(-1px)',
			boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)',
		},
	},
	switchContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginTop: theme.spacing(1),
	},
	// ✅ CORREGIDO: SwitchLabel dinámico según tema
	switchLabel: {
		fontSize: '14px',
		fontWeight: '500',
		color: theme.palette.text.primary,
	},
	// ✅ CORREGIDO: TextField dinámico según tema
	textFieldDynamic: {
		'& .MuiOutlinedInput-root': {
			backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
			color: theme.palette.text.primary,
			'& fieldset': {
				borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
			},
			'&:hover fieldset': {
				borderColor: theme.palette.primary.main,
			},
			'&.Mui-focused fieldset': {
				borderColor: theme.palette.primary.main,
			},
		},
		'& .MuiInputLabel-root': {
			color: theme.palette.text.secondary || theme.palette.text.primary,
		},
		'& .MuiInputLabel-root.Mui-focused': {
			color: theme.palette.primary.main,
		},
		'& .MuiFormHelperText-root': {
			color: theme.palette.text.secondary || theme.palette.text.primary,
		},
	},
	// ✅ CORREGIDO: FormControl dinámico según tema
	formControlDynamic: {
		'& .MuiOutlinedInput-root': {
			backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
			color: theme.palette.text.primary,
			'& fieldset': {
				borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
			},
			'&:hover fieldset': {
				borderColor: theme.palette.primary.main,
			},
			'&.Mui-focused fieldset': {
				borderColor: theme.palette.primary.main,
			},
		},
		'& .MuiInputLabel-root': {
			color: theme.palette.text.secondary || theme.palette.text.primary,
		},
		'& .MuiMenuItem-root': {
			backgroundColor: theme.palette.background.paper,
			color: theme.palette.text.primary,
			'&:hover': {
				backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#f5f5f5',
			},
		},
	},
	// ✅ CORREGIDO: HelperText dinámico según tema
	helperText: {
		fontSize: '12px',
		color: theme.palette.text.secondary || theme.palette.text.primary,
	},
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	password: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email").required("Required"),
	language: Yup.string().oneOf(["es", "en", "pt"]).required("Required"),
	// Sin rol el alta caía al camino por defecto y nacía con el rol de SISTEMA,
	// invisible para la empresa. El campo ya se pintaba como obligatorio; ahora
	// lo es de verdad.
	roleId: Yup.number().required("Required"),
});

const UserModal = ({ open, onClose, userId, onSaved }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user",
		roleId: "",
		allTicket: "disabled",
		language: "es"
	};

	const { user: loggedInUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);

	// Roles asignables de la empresa (doc 0380). Sustituyen al antiguo selector
	// "Admin / User": ese campo solo tenia dos valores, asi que SUPERVISOR no
	// podia ser el rol base de nadie y un rol propio no se podia asignar a
	// ninguna persona. Ahora se elige el ROL y el backend deriva `profile`.
	const [roles, setRoles] = useState([]);

	/**
	 * WHY las dos condiciones y no `[]`:
	 *
	 * `open` — este modal esta MONTADO SIEMPRE en el layout (para el perfil
	 * propio), asi que un efecto sin guarda pedia `/roles` en cada login de cada
	 * usuario, estuviera el modal abierto o no.
	 *
	 * `can` — el selector de rol solo lo usa quien administra usuarios. Sin el
	 * permiso, el backend responde 403 (correctamente) y el log se llenaba de
	 * avisos por una peticion que no habia por que hacer. Detectado en QA
	 * 2026-08-20: «[403] Sin permiso para roles:ver» en cada sesion de agente.
	 */
	useEffect(() => {
		if (!open || !getAccessSnapshot().can("roles:ver")) return undefined;

		let activo = true;
		(async () => {
			try {
				const { data } = await listRoles();
				if (activo) setRoles(data || []);
			} catch (err) {
				// Sin roles el selector queda vacio, pero el alta sigue siendo posible:
				// el backend acepta el camino anterior por `profile`.
				if (activo) setRoles([]);
			}
		})();
		return () => { activo = false; };
	}, [open]);
	const [tab, setTab] = useState(0);
	const [whatsappId, setWhatsappId] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [allowGroup, setAllowGroup] = useState(false); // ✅ NUEVA OPCIÓN PARA GRUPOS

	// Equipos del usuario (doc 0380 D11 · paso 2.B). Solo con el modo equipos
	// encendido: quien no trabaja por equipos no debe ver el concepto.
	const modoEquipos = getAccessSnapshot().teamsEnabled === true;
	const [equipos, setEquipos] = useState([]);
	const [misEquipos, setMisEquipos] = useState([]);
	/**
	 * Ámbito del rol elegido — la regla nueva (cliente, 2026-08-19).
	 *
	 * Se lee del catálogo y no del nombre: los roles son libres, así que atar
	 * esto a 'SUPERVISOR' se rompe en cuanto alguien cree «Jefe de turno».
	 */
	/**
	 * ¿Manda el equipo sobre sus departamentos?
	 *
	 * Solo cuando la empresa trabaja por equipos Y la persona tiene uno. Un
	 * administrador sin equipo sigue configurando lo suyo a mano.
	 */
	const mandaElEquipo = modoEquipos && misEquipos.length > 0;

	const ambitoDe = (roleId) =>
		roles.find((r) => Number(r.id) === Number(roleId))?.scope ?? null;

	const { loading, whatsApps } = useWhatsApps();
	const isMounted = useRef(true);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) {
				// ✅ LIMPIAR VALORES RESIDUALES AL CREAR NUEVO USUARIO
				if (isMounted.current) {
					setUser({
						name: "",
						email: "",
						password: "",
						profile: "user",
		roleId: "",
						allTicket: "disabled",
						language: "es",
					});
					setSelectedQueueIds([]);
					setWhatsappId('');
					setShowPassword(false);
					setAllowGroup(false);
				}
				return;
			}
			try {
				const { data } = await getUser(userId);
				if (isMounted.current) {
					setUser(prevState => {
						return {
							...prevState,
							...data,
							language: data.language || "es",
						};
					});
					const userQueueIds = data.queues?.map(queue => queue.id);
					setSelectedQueueIds(userQueueIds || []);
					setWhatsappId(data.whatsappId ? data.whatsappId : '');
					setAllowGroup(data.allowGroup || false); // ✅ CARGAR OPCIÓN DE GRUPOS
				}
			} catch (err) {
				if (isMounted.current) {
					toastError(err);
				}
			}
		};

		if (isMounted.current) {
			fetchUser();
		}
	}, [userId, open]); // eslint-disable-line react-hooks/exhaustive-deps

	// Catálogo de equipos y roles, y la pertenencia actual del usuario.
	//
	// Falla en silencio a propósito: si el usuario que edita no tiene permiso de
	// equipos, el selector se queda vacío pero la ficha sigue siendo utilizable.
	// Un permiso que falta no puede impedir editar el nombre de alguien.
	useEffect(() => {
		if (!open || !modoEquipos) return;

		let activo = true;
		(async () => {
			try {
				const { data: grupos } = await listUserGroups();
				if (!activo) return;
				setEquipos(grupos ?? []);

				if (userId) {
					const { data } = await getUserTeams(userId);
					if (!activo) return;
					setMisEquipos(
						(data ?? []).map(t => ({
							userGroupId: t.userGroupId,
							roleId: t.roleId,
							roleName: t.roleName
						}))
					);
				} else {
					setMisEquipos([]);
				}
			} catch (err) {
				// Sin permiso de equipos el selector queda vacío, pero la ficha sigue
				// siendo utilizable: un permiso que falta no puede impedir editar el
				// nombre de alguien.
				if (activo) setEquipos([]);
			}
		})();

		return () => {
			activo = false;
		};
	}, [open, userId, modoEquipos]);

	const handleClose = () => {
		// ✅ LIMPIAR COMPLETAMENTE EL FORMULARIO
		setUser({
			name: "",
			email: "",
			password: "",
			profile: "user",
		roleId: "",
			allTicket: "disabled",
			language: "es",
		});
		setSelectedQueueIds([]);
		setWhatsappId('');
		setShowPassword(false);
		setAllowGroup(false);
		setMisEquipos([]);
		setTab(0);
		onClose();
	};

	const handleSaveUser = async values => {
		const userData = { ...values, whatsappId, queueIds: selectedQueueIds, allTicket: values.allTicket, allowGroup };

		// Pertenecer a un equipo es obligatorio salvo para el administrador, que
		// ya ve toda la empresa. Se comprueba ANTES de crear nada: si no, el
		// usuario quedaría creado y sin equipo, que es el estado que esta regla
		// existe para evitar.
		const ambito = modoEquipos ? ambitoDe(values.roleId) : null;
		if (ambito && ambito !== "empresa" && misEquipos.length === 0) {
			toast.error(i18n.t("userTeams.required"));
			return;
		}

		try {
			let destinoId = userId;
			if (userId) {
				await updateUser(userId, userData);
			} else {
				const { data } = await createUser(userData);
				destinoId = data?.id;
			}

			// Los equipos van en una llamada aparte y DESPUÉS: es otra tabla, con
			// otro permiso y otras guardas. Si falla, el usuario ya quedó guardado y
			// el mensaje explica qué parte no se aplicó, en vez de perderlo todo.
			if (modoEquipos && destinoId) {
				await setUserTeams(destinoId, misEquipos);
			}

			// Se avisa DESPUÉS de guardar los equipos, no antes: el evento de socket
			// del alta lleva una vista parcial del usuario —sin equipo, sin
			// departamentos ni conexión, que se derivan al listar—, así que la fila
			// nueva salía con esas columnas vacías hasta recargar a mano.
			onSaved?.();

			toast.success(i18n.t("userModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								{/*
								  Dos pestañas: identidad y ajustes operativos. El formulario
								  tenía ocho controles seguidos y no se leía de un vistazo.

								  Los campos ocultos NO se pierden: Formik guarda los valores en
								  su estado, no en el DOM, y lo que no es de Formik vive en
								  estado local del componente. Cambiar de pestaña no borra nada.
								*/}
								<Tabs
									value={tab}
									onChange={(e, v) => setTab(v)}
									indicatorColor="primary"
									textColor="primary"
									variant="fullWidth"
									style={{ marginBottom: 16 }}
								>
									<Tab label={i18n.t("userModal.tabs.user")} />
									<Tab label={i18n.t("userModal.tabs.extra")} />
								</Tabs>

								{tab === 0 && (
									<>

								{/* ✅ SECCIÓN: INFORMACIÓN BÁSICA */}
								<div className={classes.formSection}>
									<div className={classes.sectionTitle}>
										{/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
										👤 Información Básica
									</div>
									<div className={classes.multFieldLine}>
										<TextField
											label={i18n.t("userModal.form.name")}
											autoFocus
											name="name"
											value={values.name || ""}
											onChange={(e) => setFieldValue('name', e.target.value)}
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											margin="dense"
											fullWidth
											className={classes.textFieldDynamic}
										/>
										<TextField
											name="password"
											variant="outlined"
											margin="dense"
											fullWidth
											label={i18n.t("userModal.form.password")}
											value={values.password || ""}
											onChange={(e) => setFieldValue('password', e.target.value)}
											error={touched.password && Boolean(errors.password)}
											helperText={touched.password && errors.password}
											type={showPassword ? 'text' : 'password'}
											className={classes.textFieldDynamic}
											InputProps={{
											endAdornment: (
												<InputAdornment position="end">
												<IconButton
													aria-label="toggle password visibility"
													onClick={() => setShowPassword((e) => !e)}
												>
													{showPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
												</InputAdornment>
											)
											}}
										/>
									</div>
									<div className={classes.multFieldLine}>
										<TextField
											label={i18n.t("userModal.form.email")}
											name="email"
											value={values.email || ""}
											onChange={(e) => setFieldValue('email', e.target.value)}
											error={touched.email && Boolean(errors.email)}
											helperText={touched.email && errors.email}
											variant="outlined"
											margin="dense"
											fullWidth
											className={classes.textFieldDynamic}
										/>
										<FormControl
											variant="outlined"
											className={`${classes.formControl} ${classes.formControlDynamic}`}
											margin="dense"
										>
											<Can
												role={loggedInUser.profile}
												perform="user-modal:editProfile"
												yes={() =>
													/*
													  El superadministrador NO tiene rol de empresa (doc 0380 H6):
													  su rol se otorga al instalar el producto. Como el catalogo
													  excluye el ambito `plataforma`, su `roleId` no figuraba entre
													  las opciones y el <Select> se pintaba VACIO — sin error y sin
													  aviso. Al elegir un valor y guardar tampoco pasaba nada, porque
													  `UpdateUserService` se salta la asignacion para un `super`.
													  Dos sintomas, una causa.

													  Se muestra el estado real y deshabilitado: la pantalla dice lo
													  mismo que hace el backend.
													*/
													user.super ? (
														<TextField
															label={i18n.t("userModal.form.profile")}
															value={i18n.t("userModal.form.superAdmin")}
															helperText={i18n.t("userModal.form.superAdminHint")}
															variant="outlined"
															margin="dense"
															disabled
															fullWidth
														/>
													) : (
														<>
															<InputLabel id="profile-selection-input-label">
																{i18n.t("userModal.form.profile")}
															</InputLabel>

															<Field
																as={Select}
																label={i18n.t("userModal.form.profile")}
																name="roleId"
																labelId="profile-selection-label"
																id="profile-selection"
																required
															>
																{roles.map((r) => (
																	<MenuItem key={r.id} value={r.id}>
																		{r.name}
																	</MenuItem>
																))}

																{/*
																  Candado contra el <Select> en blanco: si el rol actual
																  no esta en el catalogo —porque se desactivo o se
																  elimino—, se muestra igualmente, deshabilitado. Un
																  campo vacio no informa; uno que dice que pasa, si.
																*/}
																{/*
																  El ternario NO es cosmetico. Con `&&`, cuando
																  `user.roleId` vale "" la expresion se queda en la
																  cadena vacia, y `React.Children.toArray` CONSERVA las
																  cadenas. MUI recorre esos hijos para avisar de un valor
																  fuera de rango y hace `child.props.value`: sobre una
																  cadena, revienta. Pasaba al cerrar el modal de un
																  usuario sin rol, porque `handleClose` pone roleId: "".
																*/}
																{Boolean(user.roleId) &&
																!roles.some((r) => r.id === user.roleId) ? (
																	<MenuItem value={user.roleId} disabled>
																		{i18n.t("userModal.form.roleUnavailable")}
																	</MenuItem>
																) : null}
															</Field>
														</>
													)
												}
											/>
										</FormControl>
									</div>
									<div className={classes.multFieldLine}>
										<FormControl
											variant="outlined"
											className={`${classes.formControl} ${classes.formControlDynamic}`}
											margin="dense"
											fullWidth
										>
											<InputLabel id="user-language-label">
												{i18n.t("userModal.form.language")}
											</InputLabel>
											<Field
												as={Select}
												label={i18n.t("userModal.form.language")}
												name="language"
												labelId="user-language-label"
												id="user-language"
											>
												<MenuItem value="es">Español</MenuItem>
												<MenuItem value="en">English</MenuItem>
												<MenuItem value="pt">Português</MenuItem>
											</Field>
										</FormControl>
									</div>
								{/* Equipos: solo con el modo equipos encendido (doc 0380 D7).
								    Quien no trabaja por equipos no debe ver el concepto. */}
								{modoEquipos && (
									<UserTeamsField
										teams={equipos}
										scope={ambitoDe(values.roleId)}
										value={misEquipos}
										onChange={setMisEquipos}
										disabled={isSubmitting}
										userId={userId}
									/>
								)}

									</div>
								
</>
								)}

								{tab === 1 && (
									<>
<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (
										<>
											{/*
											  Con equipo, el departamento lo pone el equipo. Se
											  DESHABILITA, no se oculta: el histórico sigue a la
											  vista y el dato no se borra, así que apagar el modo
											  equipos lo devuelve tal cual estaba.
											*/}
											<QueueSelect
												selectedQueueIds={selectedQueueIds}
												onChange={values => setSelectedQueueIds(values)}
												disabled={mandaElEquipo}
											/>
											{mandaElEquipo && (
												<Typography variant="caption" color="textSecondary" component="div">
													{i18n.t("userModal.form.queuesFromTeam")}
												</Typography>
											)}
										</>
									)}
								/>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (
										<>
										<FormControl variant="outlined" margin="dense" className={`${classes.maxWidth} ${classes.formControlDynamic}`} fullWidth>
											<InputLabel>
												{i18n.t("userModal.form.whatsapp")}
											</InputLabel>
											<Field
												as={Select}
												value={whatsappId}
												onChange={(e) => setWhatsappId(e.target.value)}
												disabled={mandaElEquipo}
												label={i18n.t("userModal.form.whatsapp")}
											>
												<MenuItem value={''}>
													<div style={{ display: 'flex', alignItems: 'center' }}>
														<span style={{ marginRight: '8px' }} role="img" aria-label="Prohibido">❌</span>
														<span>Ninguna</span>
													</div>
												</MenuItem>
												{whatsApps.map((whatsapp) => (
													<MenuItem key={whatsapp.id} value={whatsapp.id}>
														<div style={{ display: 'flex', alignItems: 'center' }}>
															<span style={{ marginRight: '8px', fontSize: '16px' }} role="img" aria-label="WhatsApp">📱</span>
															<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
																<span style={{ fontWeight: 'bold', fontSize: '14px' }}>
																	{whatsapp.name || `ID: ${whatsapp.id}`}
																</span>
																<span style={{ fontSize: '12px', color: '#666' }}>
																	{whatsapp.waName ? `${whatsapp.name} - ${whatsapp.waName}` : 
																	 whatsapp.name ? whatsapp.name : 
																	 `Conexión ${whatsapp.id}`}
																</span>
															</div>
														</div>
													</MenuItem>
												))}
											</Field>
										</FormControl>
										<Typography variant="caption" color="textSecondary" component="div">
											{i18n.t("userModal.form.whatsappHint")}
										</Typography>
										</>
									)}
								/>										
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (!loading &&
										<div className={classes.textField}>
											<FormControl
												variant="outlined"
												className={`${classes.maxWidth} ${classes.formControlDynamic}`}
												margin="dense"
												fullWidth
											>
												<>
													<InputLabel id="profile-selection-input-label">
														Ver Tickets Sin Asignar
													</InputLabel>

													<Field
														as={Select}
														label="Ver Tickets Sin Asignar"
														name="allTicket"
														labelId="allTicket-selection-label"
														id="allTicket-selection"
														disabled={mandaElEquipo}
														required
													>
														<MenuItem value="enabled">Habilitado</MenuItem>
														<MenuItem value="disabled">Deshabilitado</MenuItem>
													</Field>
												</>
											</FormControl>
											<div className={classes.helperText} style={{ marginTop: '4px', padding: '0 8px' }}>
												<strong>Habilitado:</strong> Puede ver tickets que no están asignados a ningún departamento<br/>
												<strong>Deshabilitado:</strong> Solo ve tickets de sus departamentos asignados
											</div>
										</div>

									)}
								/>
								{/* ✅ NUEVA OPCIÓN: PERMITIR GRUPOS */}
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (
										<FormControlLabel
											control={
												<Switch
													checked={allowGroup}
												disabled={mandaElEquipo}
													onChange={(e) => setAllowGroup(e.target.checked)}
													name="allowGroup"
													color="primary"
												/>
											}
											label="Permitir Grupos de WhatsApp"
											style={{ marginTop: 16 }}
										/>
									)}
								/>

								
									</>
								)}
							</DialogContent>
							<DialogActions className={classes.btnWrapper}>
								<Button
									onClick={handleClose}
									disabled={isSubmitting}
									className={classes.cancelButton}
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									disabled={isSubmitting}
									className={classes.saveButton}
								>
									{userId
										? `${i18n.t("userModal.buttons.okEdit")}`
										: `${i18n.t("userModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default UserModal;
